'use client';

import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Menu, Search, MapPin, ChevronDown, ChevronRight, ChevronLeft, X, User, LogIn, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NavCategory, ServiceList, DocumentList } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { LoginModal } from '@/components/ui/LoginModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ServiceHeaderActions } from '@/components/layout/ServiceHeaderActions';
import { useNativeApp } from '@/lib/native';
import { useAdvisorButtonOptional } from '@/contexts/AdvisorButtonContext';

interface City {
  id: string;
  cityName: string;
  urlStructure: string;
}

interface AppHeaderProps {
  cities?: City[];
  navigation?: NavCategory[];
  selectedCity?: City | null;
  onCityChange?: (city: City) => void;
  services?: ServiceList[];
  documents?: DocumentList[];
}

const STATIC_TOP_PATHS = new Set([
  'about',
  'blog',
  'cart',
  'categories',
  'cities',
  'contact',
  'dashboard',
  'documents',
  'login',
  'privacy-policy',
  'signup',
  'terms-of-services',
]);

const toUrlSlug = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export function AppHeader({ cities = [], navigation = [], selectedCity, onCityChange, services = [], documents = [] }: AppHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { isAuthenticated, user, logout } = useAuth();
  const { isNative } = useNativeApp();

  const router = useRouter();
  const pathname = usePathname();
  const showBackButton = isNative && pathname !== '/';

  // Lead-capture "thank you" overlay state — hide the city behind it.
  const advisor = useAdvisorButtonOptional();
  const isLeadThankYou = advisor?.isLeadThankYou ?? false;

  // Reset the city search box whenever the dropdown closes.
  useEffect(() => {
    if (!isCityDropdownOpen) setCitySearchQuery('');
  }, [isCityDropdownOpen]);

  // Filter the city dropdown list by the in-dropdown search box.
  const filteredCities = useMemo(() => {
    const q = citySearchQuery.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((city) => city.cityName.toLowerCase().includes(q));
  }, [cities, citySearchQuery]);

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
    } else {
      router.push('/');
    }
  };

  // Classify the current route so the native header can be tailored per page:
  //   city      → /[city]
  //   category  → /[city]/[category]
  //   document  → /[city]/[category]/[document]
  //   service   → /[city]/[category]/[document]/[service]
  // Only city-rooted content pages are reclassified; static pages (/blog,
  // /categories, …) stay 'other' and keep the default header.
  const pageVariant = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return 'home';
    const firstIsCity = cities.some(
      (c) => c.urlStructure === segments[0] || toUrlSlug(c.cityName) === segments[0]
    );
    if (!firstIsCity) return 'other';
    if (segments.length === 1) return 'city';
    if (segments.length === 2) return 'category';
    if (segments.length === 3) return 'document';
    return 'service';
  }, [pathname, cities]);

  // On a service page, resolve the current service (for favourite + share)
  // by matching the URL slugs against the city's service list.
  const currentService = useMemo(() => {
    if (pageVariant !== 'service') return null;
    const [, categorySlug, documentSlug, serviceSlug] = pathname
      .split('/')
      .filter(Boolean);
    return (
      services.find(
        (s) =>
          s.serviceSlug?.categorySlug === categorySlug &&
          s.serviceSlug?.documentSlug === documentSlug &&
          s.serviceSlug?.serviceSlug === serviceSlug
      ) || null
    );
  }, [pageVariant, pathname, services]);

  // Native content pages (category / document / service) drop the search,
  // menu and notification bell. Thank-you also hides everything (incl. city).
  const isServicePage = pageVariant === 'service';
  const isMinimalPage =
    pageVariant === 'category' || pageVariant === 'document' || isServicePage;
  const hideStandardActions = isNative && (isMinimalPage || isLeadThankYou);
  const hideCity = isNative && isLeadThankYou;

  const ALIAS_MAP: Record<string, string[]> = {
    aadhaar: ['aadhaar', 'aadhar', 'uidai', 'adhar', 'aadhaar card', 'aadhar card'],
    dl: ['dl', 'driving license', 'driver license', 'driving licence', 'license', 'licence'],
    passport: ['passport', 'pass port', 'indian passport'],
    pan: ['pan', 'pan card', 'permanent account number'],
    voter: ['voter', 'voter id', 'election card', 'voter card'],
    birth: ['birth certificate', 'birth cert', 'dob certificate'],
    marriage: ['marriage certificate', 'marriage cert'],
  };

  // Build set of document slugs available in the current city's services
  const cityDocumentSlugs = useMemo(() => {
    const slugs = new Set<string>();
    for (const svc of services) {
      if (svc.serviceSlug?.documentSlug) {
        slugs.add(svc.serviceSlug.documentSlug);
      }
    }
    return slugs;
  }, [services]);

  // Only show documents that have at least one service in the current city
  const cityDocuments = useMemo(() => {
    if (cityDocumentSlugs.size === 0) return [];
    return documents.filter((d) => cityDocumentSlugs.has(d.documentSlug));
  }, [documents, cityDocumentSlugs]);

  // Client-side filtering with alias support
  const input = searchQuery.toLowerCase().trim();

  const filteredServices = useMemo(() => {
    if (!input) return [];
    return services.filter((s) => {
      const serviceName = s.service?.toLowerCase() || '';
      const docName = s.document?.document?.toLowerCase() || '';
      return (
        serviceName.includes(input) ||
        docName.includes(input) ||
        Object.values(ALIAS_MAP).some(
          (aliases) =>
            aliases.some((a) => a.includes(input) || input.includes(a)) &&
            aliases.some((alias) => serviceName.includes(alias) || docName.includes(alias))
        )
      );
    });
  }, [input, services]);

  const filteredDocuments = useMemo(() => {
    if (!input) return [];
    return cityDocuments.filter((d) => {
      const docName = d.document?.toLowerCase() || '';
      return (
        docName.includes(input) ||
        Object.values(ALIAS_MAP).some(
          (aliases) =>
            aliases.some((a) => a.includes(input) || input.includes(a)) &&
            aliases.some((alias) => docName.includes(alias))
        )
      );
    });
  }, [input, cityDocuments]);

  // Group services by category (use category, then document category, then document name)
  const groupedServices = useMemo(() => {
    return filteredServices.reduce<Record<string, typeof services>>((acc, service) => {
      const category =
        service.category ||
        service.document?.category?.category ||
        service.document?.document ||
        'Services';
      if (!acc[category]) acc[category] = [];
      acc[category].push(service);
      return acc;
    }, {});
  }, [filteredServices]);

  // Group documents by category
  const groupedDocuments = useMemo(() => {
    return filteredDocuments.reduce<Record<string, typeof documents>>((acc, doc) => {
      const category = doc.category || 'Documents';
      if (!acc[category]) acc[category] = [];
      acc[category].push(doc);
      return acc;
    }, {});
  }, [filteredDocuments]);

  // Related services: services from same documents as matched services, excluding direct matches
  const relatedServices = useMemo(() => {
    if (filteredServices.length === 0 || !input) return [];
    const documentNames = new Set(filteredServices.map((s) => s.document?.document).filter(Boolean));
    const filteredIds = new Set(filteredServices.map((s) => s.id));
    return services.filter(
      (s) => documentNames.has(s.document?.document) && !filteredIds.has(s.id)
    );
  }, [filteredServices, services, input]);

  const groupedRelatedServices = useMemo(() => {
    return relatedServices.reduce<Record<string, typeof services>>((acc, service) => {
      const category =
        service.category ||
        service.document?.category?.category ||
        service.document?.document ||
        'Related Services';
      if (!acc[category]) acc[category] = [];
      acc[category].push(service);
      return acc;
    }, {});
  }, [relatedServices]);

  const hasResults = filteredServices.length > 0 || filteredDocuments.length > 0 || relatedServices.length > 0;

  // Default services to show when search is open but no query
  const defaultServices = useMemo(() => {
    return services.slice(0, 8);
  }, [services]);

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const inSearchIcon = searchRef.current?.contains(target);
      const inSearchPanel = searchPanelRef.current?.contains(target);
      if (isSearchOpen && !inSearchIcon && !inSearchPanel) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
      if (!target.closest('[data-dropdown]')) {
        setIsCityDropdownOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const currentCitySlug = useMemo(() => {
    if (selectedCity?.urlStructure) return selectedCity.urlStructure;
    if (selectedCity?.cityName) return toUrlSlug(selectedCity.cityName);
    return 'bangalore';
  }, [selectedCity]);

  const buildHref = useMemo(() => {
    return (href?: string) => {
      if (!href) return '#';
      if (href.startsWith('http://') || href.startsWith('https://')) return href;

      const normalized = href.startsWith('/') ? href : `/${href}`;
      const [firstSegment] = normalized.split('/').filter(Boolean);

      if (firstSegment === currentCitySlug || STATIC_TOP_PATHS.has(firstSegment)) {
        return normalized;
      }

      return `/${currentCitySlug}${normalized}`;
    };
  }, [currentCitySlug]);

  const processedNavigation = useMemo(() => {
    return navigation.map((category) => ({
      ...category,
      href: category.href ? buildHref(category.href) : undefined,
      items: (category.items || []).map((item) => ({
        ...item,
        href: buildHref(item.href),
      })),
    }));
  }, [navigation, buildHref]);

  return (
    <>
      <header className="app-header bg-background border-b z-50">
        <div className="container flex h-full items-center gap-4 relative">
          {/* Logo — hidden in native app; back button takes its place. */}
          {!isNative && (
            <Link href="/" className="flex items-center gap-2 shrink-0">
              <img
                src="/docuproHeader.png"
                alt="DocuPro"
                className="h-8 md:h-9 w-auto"
                width={120}
                height={32}
              />
            </Link>
          )}

          {/* Back button in the logo slot (native app only, not on home page). */}
          {showBackButton && (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1 text-sm font-medium text-foreground active:opacity-60 shrink-0"
              aria-label="Go back"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
          )}

          {/* City selector centered in the header (native app only).
              Absolutely positioned so it stays truly centered regardless
              of back-button or right-actions width. Hidden behind the
              lead-capture thank-you overlay. */}
          {isNative && !hideCity && (
            <div className="absolute inset-x-0 top-0 bottom-0 flex items-center justify-center pointer-events-none">
              <div className="relative pointer-events-auto" data-dropdown>
                <button
                  onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                  className="city-selector h-9"
                  aria-expanded={isCityDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <MapPin className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate text-xs font-medium">
                    {selectedCity?.cityName || 'Select City'}
                  </span>
                  <ChevronDown className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform shrink-0",
                    isCityDropdownOpen && "rotate-180"
                  )} />
                </button>

                {isCityDropdownOpen && cities.length > 0 && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-60 bg-background border border-border rounded-xl shadow-lg max-h-[60vh] overflow-y-auto z-50 animate-scale-in">
                    <div className="p-2 sticky top-0 bg-background border-b z-10">
                      <input
                        type="text"
                        placeholder="Search city..."
                        value={citySearchQuery}
                        onChange={(e) => setCitySearchQuery(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-muted rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredCities.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-muted-foreground">No cities found</div>
                    ) : (
                      filteredCities.map((city) => (
                        <button
                          key={city.id}
                          onClick={() => {
                            onCityChange?.(city);
                            setIsCityDropdownOpen(false);
                          }}
                          className={cn(
                            "w-full px-4 py-3 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2",
                            selectedCity?.id === city.id && "bg-primary/10 text-primary font-medium"
                          )}
                        >
                          <MapPin className="w-4 h-4" />
                          {city.cityName}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Desktop Navigation — centered */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-6" data-dropdown>
            {processedNavigation.map((category, index) => {
              // Force About Us and Blogs to always be simple links (not dropdowns)
              const isAboutOrBlogs = category.label === 'About Us' || category.label === 'Blogs';
              
              // Hide About Us and Blogs for non-Hyderabad cities
              if (isAboutOrBlogs && currentCitySlug !== 'hyderabad') {
                return null;
              }
              
              // About Us and Blogs are always simple links
              if (isAboutOrBlogs) {
                const href = category.label === 'About Us' 
                  ? '/about' 
                  : '/blog';
                return (
                  <Link
                    key={`${category.label}-${index}`}
                    href={href}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    {category.label}
                  </Link>
                );
              }
              
              // Other items without subitems are also simple links
              if (!category.items || category.items.length === 0) {
                return (
                  <Link
                    key={`${category.label}-${index}`}
                    href={category.href || '#'}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                  >
                    {category.label}
                  </Link>
                );
              }
              
              // Dropdown items
              return (
                <div key={`${category.label}-${index}`} className="relative group">
                  <button
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                    onClick={() => setActiveCategory(activeCategory === category.label ? null : category.label)}
                    onMouseEnter={() => setActiveCategory(category.label)}
                  >
                    {category.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {/* Dropdown Menu */}
                  {activeCategory === category.label && (
                    <div
                      className="absolute top-full left-0 mt-2 w-64 bg-background border border-border rounded-xl shadow-lg p-2 z-50 animate-fade-up"
                      onMouseLeave={() => setActiveCategory(null)}
                    >
                      {category.items.map((item, itemIndex) => (
                        <Link
                          key={`${item.href}-${itemIndex}`}
                          href={item.href || '#'}
                          className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                          onClick={() => setActiveCategory(null)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4 shrink-0 ml-auto lg:ml-0">
            {/* City Selector — only on web/desktop. In native it's already
                rendered on the left in place of the logo. */}
            {!isNative && (
            <div className="relative max-w-[140px] md:max-w-[180px]" data-dropdown>
              <button
                onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                className="city-selector w-full h-9 md:h-10"
                aria-expanded={isCityDropdownOpen}
                aria-haspopup="listbox"
              >
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                <span className="truncate text-xs md:text-sm">
                  {selectedCity?.cityName || 'Select City'}
                </span>
                <ChevronDown className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform shrink-0",
                  isCityDropdownOpen && "rotate-180"
                )} />
              </button>

              {/* City Dropdown */}
              {isCityDropdownOpen && cities.length > 0 && (
                <div className="absolute top-full right-0 mt-1 w-60 bg-background border border-border rounded-xl shadow-lg max-h-[60vh] overflow-y-auto z-50 animate-scale-in">
                  <div className="p-2 sticky top-0 bg-background border-b z-10">
                    <input
                      type="text"
                      placeholder="Search city..."
                      value={citySearchQuery}
                      onChange={(e) => setCitySearchQuery(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-muted rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredCities.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground">No cities found</div>
                  ) : (
                    filteredCities.map((city) => (
                      <button
                        key={city.id}
                        onClick={() => {
                          onCityChange?.(city);
                          setIsCityDropdownOpen(false);
                        }}
                        className={cn(
                          "w-full px-4 py-3 text-left text-sm hover:bg-muted transition-colors flex items-center gap-2",
                          selectedCity?.id === city.id && "bg-primary/10 text-primary font-medium"
                        )}
                      >
                        <MapPin className="w-4 h-4" />
                        {city.cityName}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            )}

            {/* Service page (native): favourite + share replace search & menu. */}
            {isNative && isServicePage && !isLeadThankYou && (
              <ServiceHeaderActions
                serviceId={currentService?.id ? Number(currentService.id) : undefined}
                serviceName={currentService?.service}
                onRequireLogin={() =>
                  router.push(`/login?callbackUrl=${encodeURIComponent(pathname)}`)
                }
              />
            )}

            {/* Search Icon — hidden on native content/thank-you pages. */}
            {!hideStandardActions && (
              <div ref={searchRef}>
                <button
                  onClick={() => {
                    setIsSearchOpen(!isSearchOpen);
                    if (isSearchOpen) setSearchQuery('');
                  }}
                  className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                  aria-label="Search"
                >
                  {isSearchOpen ? (
                    <X className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Search className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
              </div>
            )}

            {/* Login Button - Desktop */}
            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <NotificationBell />
                <Link
                  href="/account"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                >
                  <User className="w-4 h-4" />
                  {user?.name || 'Account'}
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-sm font-medium"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => router.push('/login')}
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-sm font-medium"
              >
                <LogIn className="w-4 h-4" />
                Login
              </button>
            )}

            {/* Notification Bell - Mobile (desktop bell lives in the auth block above) */}
            {isAuthenticated && !hideStandardActions && <NotificationBell className="md:hidden relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors" />}

            {/* Menu Button - Mobile — hidden on native content/thank-you pages. */}
            {!hideStandardActions && (
              <button
                onClick={() => setIsMenuOpen(true)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Search Panel — slides below header */}
      {isSearchOpen && (
        <div
          className="fixed top-[calc(var(--header-height)+var(--safe-area-inset-top))] left-0 right-0 bottom-0 z-[80]"
          onClick={(e) => {
            // Close if clicking the backdrop (not the inner panel)
            if (e.target === e.currentTarget) {
              setIsSearchOpen(false);
              setSearchQuery('');
            }
          }}
        >
          <div className="flex justify-center">
            <div ref={searchPanelRef} className="w-full max-w-3xl px-4 py-3">
            {/* Search Input */}
            <div
              className="flex items-center h-12 bg-white rounded-md border border-gray-300 shadow-md transition-all duration-200 focus-within:border-primary"
            >
              <div className="pl-4">
                <Search className="w-4 h-4 text-muted-foreground" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search for a service or document..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }
                  if (e.key === 'Enter') {
                    if (filteredServices.length > 0) {
                      const s = filteredServices[0];
                      if (s.serviceSlug) {
                        const { categorySlug, documentSlug, serviceSlug } = s.serviceSlug;
                        router.push(`/${currentCitySlug}/${categorySlug}/${documentSlug}/${serviceSlug}`);
                      }
                      setIsSearchOpen(false);
                      setSearchQuery('');
                    }
                  }
                }}
                className="flex-1 px-3 mx-2 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder-gray-500 font-medium text-gray-900"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    searchInputRef.current?.focus();
                  }}
                  className="pr-3 text-gray-400 hover:text-gray-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Results Dropdown */}
            <div className="mt-0.5 bg-white border border-gray-200 rounded-lg shadow-xl max-h-[40vh] overflow-y-auto">
              {input.length > 0 ? (
                hasResults ? (
                  <ul className="py-1">
                    {/* Services grouped by category */}
                    {Object.entries(groupedServices).map(([category, items]) => (
                      <li key={`svc-${category}`}>
                        <div className="text-xs font-semibold text-primary uppercase tracking-wider px-3 py-1.5">
                          {category}
                        </div>
                        <ul>
                          {items.map((service) => {
                            let href = '#';
                            if (service.serviceSlug) {
                              const { categorySlug, documentSlug, serviceSlug } = service.serviceSlug;
                              href = `/${currentCitySlug}/${categorySlug}/${documentSlug}/${serviceSlug}`;
                            }
                            return (
                              <li key={`svc-${service.id}`}>
                                <Link
                                  href={href}
                                  onClick={() => {
                                    setIsSearchOpen(false);
                                    setSearchQuery('');
                                  }}
                                  className="block px-3 py-2 text-sm text-gray-700 hover:bg-primary/5 rounded-md cursor-pointer"
                                >
                                  <div className="font-medium text-gray-900">
                                    {service.service}
                                  </div>
                                  {service.document?.document && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Document: {service.document.document}
                                    </div>
                                  )}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}

                    {/* Documents grouped by category */}
                    {Object.entries(groupedDocuments).map(([category, items]) => (
                      <li key={`doc-${category}`}>
                        <div className="text-xs font-semibold text-green-700 uppercase tracking-wider px-4 py-2 bg-green-50 border-b border-green-100">
                          {category}
                        </div>
                        <ul>
                          {items.map((doc) => {
                            const catSlug = doc.category?.toLowerCase().replace(/\s+/g, '-') || '';
                            const docHref = `/${currentCitySlug}/${catSlug}/${doc.documentSlug}`;
                            return (
                              <li key={`doc-${doc.id}`}>
                                <Link
                                  href={docHref}
                                  onClick={() => {
                                    setIsSearchOpen(false);
                                    setSearchQuery('');
                                  }}
                                  className="block px-4 py-3 hover:bg-green-50 hover:text-green-700 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900">
                                    {doc.document}
                                  </div>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}

                    {/* Related Services */}
                    {Object.entries(groupedRelatedServices).map(([category, items]) => (
                      <li key={`related-${category}`}>
                        <div className="text-xs font-semibold text-purple-700 uppercase tracking-wider px-4 py-2 bg-purple-50 border-b border-purple-100">
                          Related Services - {category}
                        </div>
                        <ul>
                          {items.map((service) => {
                            let href = '#';
                            if (service.serviceSlug) {
                              const { categorySlug, documentSlug, serviceSlug } = service.serviceSlug;
                              href = `/${currentCitySlug}/${categorySlug}/${documentSlug}/${serviceSlug}`;
                            }
                            return (
                              <li key={`related-${service.id}`}>
                                <Link
                                  href={href}
                                  onClick={() => {
                                    setIsSearchOpen(false);
                                    setSearchQuery('');
                                  }}
                                  className="block px-4 py-3 hover:bg-purple-50 hover:text-purple-700 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900">
                                    {service.service}
                                  </div>
                                  {service.document?.document && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      From document: {service.document.document}
                                    </div>
                                  )}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="py-8 text-center text-gray-500 font-medium">
                    No services or documents found.
                  </div>
                )
              ) : (
                /* Default state: show popular searches + a few services */
                <div className="py-2">
                  <div className="px-4 py-2">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Popular Searches</p>
                    <div className="flex flex-wrap gap-2">
                      {['Passport', 'PAN Card', 'Driving License', 'Aadhaar', 'Birth Certificate'].map((term) => (
                        <button
                          key={term}
                          onClick={() => setSearchQuery(term)}
                          className="px-3 py-1.5 text-xs bg-gray-100 rounded-md hover:bg-gray-200 transition-colors font-medium text-gray-700"
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                  {defaultServices.length > 0 && (
                    <div className="mt-2 border-t border-gray-100">
                      <div className="text-xs font-semibold text-primary uppercase tracking-wider px-3 py-1.5 mt-1">
                        Popular Services
                      </div>
                      <ul>
                        {defaultServices.map((service) => {
                          let href = '#';
                          if (service.serviceSlug) {
                            const { categorySlug, documentSlug, serviceSlug } = service.serviceSlug;
                            href = `/${currentCitySlug}/${categorySlug}/${documentSlug}/${serviceSlug}`;
                          }
                          return (
                            <li key={`default-${service.id}`}>
                              <Link
                                href={href}
                                onClick={() => {
                                  setIsSearchOpen(false);
                                  setSearchQuery('');
                                }}
                                className="block px-3 py-2 text-sm text-gray-700 hover:bg-primary/5 rounded-md cursor-pointer"
                              >
                                <div className="font-medium text-gray-900">
                                  {service.service}
                                </div>
                                {service.document?.document && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Document: {service.document.document}
                                  </div>
                                )}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Drawer */}
      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[90] animate-fade-in"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-[280px] bg-background z-[100] animate-slide-in-right shadow-xl" style={{ paddingTop: 'var(--safe-area-inset-top)' }}>
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-semibold">Menu</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
              <Link
                href="/"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>

              {processedNavigation.map((category, index) => {
                const isAboutOrBlogs = category.label === 'About Us' || category.label === 'Blogs';
                if (isAboutOrBlogs && currentCitySlug !== 'hyderabad') return null;

                if (isAboutOrBlogs || !category.items || category.items.length === 0) {
                  const href = category.label === 'About Us'
                    ? '/about'
                    : category.label === 'Blogs'
                    ? '/blog'
                    : category.href || '#';
                  return (
                    <Link
                      key={`mobile-${category.label}-${index}`}
                      href={href}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {category.label}
                    </Link>
                  );
                }

                const isExpanded = expandedMobileCategory === category.label;
                return (
                  <div key={`mobile-${category.label}-${index}`}>
                    <button
                      onClick={() => setExpandedMobileCategory(isExpanded ? null : category.label)}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                    >
                      {category.label}
                      <ChevronRight className={cn('w-4 h-4 transition-transform', isExpanded && 'rotate-90')} />
                    </button>
                    {isExpanded && (
                      <div className="ml-4 space-y-0.5">
                        {category.items.map((item, itemIndex) => (
                          <Link
                            key={`mobile-item-${itemIndex}`}
                            href={item.href || '#'}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <Link
                href="/blog"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                href="/about"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              <Link
                href="/contact"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground hover:bg-muted transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t" style={{ paddingBottom: 'calc(1rem + var(--safe-area-bottom))' }}>
              {isAuthenticated ? (
                <div className="space-y-2">
                  <Link
                    href="/account"
                    className="flex items-center justify-center gap-2 w-full h-12 bg-primary text-white rounded-xl font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="w-5 h-5" />
                    {user?.name || 'My Account'}
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center justify-center gap-2 w-full h-12 border border-border rounded-xl font-medium"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    router.push('/login');
                  }}
                  className="flex items-center justify-center gap-2 w-full h-12 bg-primary text-white rounded-xl font-medium"
                >
                  <LogIn className="w-5 h-5" />
                  Login
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  );
}
